/**
 * Calendar Data Loader
 *
 * Loads calendar data with rich event details from JSON files using lazy loading.
 * Falls back to basic data from calendars.js for calendars without JSON files.
 *
 * PERFORMANCE OPTIMIZATION:
 * - JSON files are lazy-loaded using dynamic import() instead of eager static imports
 * - Reduces initial bundle size by ~968KB
 * - Data is cached after first load for instant subsequent access
 */

import { THEMED_CALENDARS, CATEGORIES } from '../../config/calendars';

// Cache for lazy-loaded detailed calendars
const detailedCalendarsCache = new Map();

// List of calendar IDs that have detailed JSON files
// This allows us to check availability without loading the data
const CALENDARS_WITH_JSON = [
  'formula-1',
  'rich-bitch',
  'tech-bro',
  'festival-fiend',
  'sports-superfan',
  'wine-mom',
  'suburban-dad',
  'kpop-stan',
  'disney-adult',
  'jewish-holidays',
  'all-american',
  'rock-metal',
  'potterhead',
  'broadway-baby',
  'high-season',
  'pride',
  'latino-heritage',
  'wellness-warrior',
  'chronically-online',
  'black-excellence',
  'lunar-aapi',
  'millennial-nostalgia',
  'vegas-regular',
  'space-enthusiast',
  'country-music',
  'shopping-seasons',
  'renaissance-faires',
  'fiber-arts',
];

/**
 * Get a calendar with enriched event details (ASYNC - lazy loaded)
 * @param {string} calendarId - The calendar ID
 * @returns {Promise<object>} - Calendar object with detailed events
 */
export async function getCalendarWithDetails(calendarId) {
  // Check cache first
  if (detailedCalendarsCache.has(calendarId)) {
    return detailedCalendarsCache.get(calendarId);
  }

  // Check if this calendar has a JSON file
  if (CALENDARS_WITH_JSON.includes(calendarId)) {
    try {
      // Lazy load the JSON file
      const module = await import(`./${calendarId}.json`);
      const detailedData = module.default;

      // Cache the result
      detailedCalendarsCache.set(calendarId, detailedData);

      return detailedData;
    } catch (error) {
      console.error(`Failed to load detailed data for calendar: ${calendarId}`, error);
      // Fall through to basic calendar fallback
    }
  }

  // Fall back to basic data from calendars.js
  const basicCalendar = THEMED_CALENDARS.find(cal => cal.id === calendarId);

  if (!basicCalendar) {
    throw new Error(`Calendar not found: ${calendarId}`);
  }

  // Return basic calendar with empty detail objects for each event
  const fallbackCalendar = {
    ...basicCalendar,
    heroImage: null,
    faq: [],
    relatedCalendars: [],
    events: basicCalendar.events.map((event, idx) => ({
      id: `${calendarId}-event-${idx}`,
      ...event,
      detail: null, // No detailed info available
    })),
  };

  // Cache the fallback
  detailedCalendarsCache.set(calendarId, fallbackCalendar);

  return fallbackCalendar;
}

/**
 * Get a calendar with basic events (SYNCHRONOUS - from calendars.js)
 * Use this when you need synchronous access and don't need extended details
 * @param {string} calendarId - The calendar ID
 * @returns {object|null} - Basic calendar object with events, or null if not found
 */
export function getBasicCalendar(calendarId) {
  return THEMED_CALENDARS.find(cal => cal.id === calendarId) || null;
}

/**
 * Get event with full details by event ID (ASYNC)
 * @param {string} calendarId - The calendar ID
 * @param {string} eventId - The event ID
 * @returns {Promise<object|null>} - Event object with details, or null if not found
 */
export async function getEventWithDetails(calendarId, eventId) {
  const calendar = await getCalendarWithDetails(calendarId);

  if (!calendar) {
    return null;
  }

  const event = calendar.events.find(e => e.id === eventId);

  if (!event) {
    return null;
  }

  return {
    ...event,
    calendarId: calendar.id,
    calendarName: calendar.name,
    calendarEmoji: calendar.emoji,
    calendarColor: calendar.color,
  };
}

/**
 * Get all events from a calendar for navigation (ASYNC)
 * @param {string} calendarId - The calendar ID
 * @returns {Promise<array>} - Array of events with basic info for carousel navigation
 */
export async function getCalendarEvents(calendarId) {
  const calendar = await getCalendarWithDetails(calendarId);

  if (!calendar) {
    return [];
  }

  return calendar.events.map(event => ({
    id: event.id,
    name: event.name,
    startDate: event.startDate || event.date,
    endDate: event.endDate,
    location: event.location,
    hasDetail: !!event.detail,
    isHighlight: event.detail?.isHighlight || false,
  }));
}

/**
 * Get index of event in calendar's event list (ASYNC)
 * @param {string} calendarId - The calendar ID
 * @param {string} eventId - The event ID
 * @returns {Promise<number>} - Index of event, or -1 if not found
 */
export async function getEventIndex(calendarId, eventId) {
  const calendar = await getCalendarWithDetails(calendarId);

  if (!calendar) {
    return -1;
  }

  return calendar.events.findIndex(e => e.id === eventId);
}

/**
 * Check if a calendar has detailed event data
 * @param {string} calendarId - The calendar ID
 * @returns {boolean} - True if calendar has JSON data with event details
 */
export function hasDetailedData(calendarId) {
  return CALENDARS_WITH_JSON.includes(calendarId);
}

/**
 * Get list of calendars with detailed data available
 * @returns {array} - Array of calendar IDs that have detailed JSON data
 */
export function getCalendarsWithDetails() {
  return [...CALENDARS_WITH_JSON];
}

/**
 * Prefetch calendar details for better UX
 * Useful for preloading on hover/focus before user clicks
 * @param {string} calendarId - The calendar ID
 * @returns {Promise<void>} - Resolves when prefetch completes (or fails silently)
 */
export function preloadCalendarDetails(calendarId) {
  return getCalendarWithDetails(calendarId).catch(() => {
    // Fail silently - this is just a prefetch
  });
}

/**
 * Clear the cache (useful for testing or memory management)
 */
export function clearCache() {
  detailedCalendarsCache.clear();
}

/**
 * Get cache statistics (for debugging)
 * @returns {object} - Cache size and list of cached calendar IDs
 */
export function getCacheStats() {
  return {
    size: detailedCalendarsCache.size,
    calendars: Array.from(detailedCalendarsCache.keys())
  };
}

/**
 * Get category info by ID
 * @param {string} categoryId - The category ID
 * @returns {object|null} - Category object or null
 */
export function getCategoryInfo(categoryId) {
  return CATEGORIES[categoryId] || null;
}

export { CATEGORIES };
